import { useRecoilState, useRecoilValue,  } from "recoil";
import {
  openQuantityComponentModalState,
  productQuantityTypesState,
  productQuantityTypesValuesState,
  tempProductQuantityTypesValuesState,
} from "@/pages-components/products/digital-offset-price/widgets/render-parameter-widgets/quantity-parameter/quantity-types/state";
import { IInput } from "@/components/form-inputs/interfaces";
import { useCallback, useEffect, useMemo, useState } from "react";
import { quantityParameterState, subProductsParametersState } from "@/store";
import { getParameterByParameterCode } from "@/utils/constants";
import { useDebouncedCallback } from "use-debounce";
import lodashClonedeep from "lodash.clonedeep";

interface QuantityTypesInputs extends IInput {
  onChange: (key, value: string) => void;
  readonly?: boolean;
}
const useQuantityTypes = ({subSection, section}) => {
  const [save, setSave] = useState<boolean>(false);
  const [quantityState, setQuantityState] = useRecoilState(
    productQuantityTypesState
  );
  const quantityParameter = useRecoilValue<number>(quantityParameterState);
  const [subProducts, setSubProducts] = useRecoilState<any>(subProductsParametersState);
  const initialTypesValues = subProducts[0]?.parameters.find((param) => param.parameterId === "de2bb7d5-01b1-4b2b-b0fa-81cd0445841b")?.values.join(", ");
  const [newTypesValue, setNewTypesValue] = useState(initialTypesValues);
  const [quantityTypes, setQuantityTypes] = useRecoilState(productQuantityTypesValuesState);
  const JobNameParameter = getParameterByParameterCode(subProducts,"JobName");
  const [openModal, setOpenModal] = useRecoilState<boolean>(openQuantityComponentModalState);
  useEffect(() => {
    if (quantityParameter === 0) {
      const newParameter = {
        parameterId: "4991945c-5e07-4773-8f11-2e3483b70b53",
        sectionId: section?.id,
        subSectionId: subSection?.id,
        parameterType: 1,
        parameterName: "Quantity",
        actionId: null,
        values: ["0"],
        valueIds: [],
        actionIndex: 0,
        parameterCode: "quantity",
        valuesConfigs: [],
        unitKey: null,
        unitType: 0,
      };

      // Check if this parameter is already in the subProducts array
      const updatedSubProducts = subProducts.map((subProduct) => {
        if (subProduct.sectionId === section?.id) {
          const existingParameter = subProduct?.parameters?.find(
            (param) => param.parameterCode === "quantity"
          );
          if (!existingParameter) {
            return {
              ...subProduct,
              parameters: [...subProduct.parameters, newParameter],
            };
          }
        }
        return subProduct;
      });

      setSubProducts(updatedSubProducts);
    }
  }, [quantityParameter ]);
  const updateTypesValues = (newValues) => {
    const updatedSubProducts = lodashClonedeep(subProducts);
    const typesParameter = updatedSubProducts[0]?.parameters.find(
      (param) => param.parameterCode === "types"
    );

    if (typesParameter) {
      typesParameter.values = [newValues];
      setSubProducts(updatedSubProducts);
    }
    setOpenModal(false)
  };
  const debouncedHandleInputChange = useDebouncedCallback((myvalue) => {
    if (quantityTypesValues.length === Number(myvalue)) {
      setValuesState(quantityTypesValues);
    } else if (quantityTypesValues.length < Number(myvalue)) {
      const array = [];
      for (let i = quantityTypesValues.length + 1; i <= Number(myvalue); i++) {
        array.push({
          name: JobNameParameter?.values[0] + " " + i,
          quantity: +valueState[0]?.quantity||0,
        });
      }
      setValuesState([...quantityTypesValues, ...array]);
    } else if (quantityTypesValues.length > Number(myvalue)) {
      setValuesState(quantityTypesValues.slice(0, Number(myvalue)));
    }
  }, 700);

  const handleInputChange = (event,value) => {
    setNewTypesValue(value); // Update state immediately
    debouncedHandleInputChange(value); // Apply debounced logic
  };

  useEffect(() => {
    if (save) {
      updateTypesValues(newTypesValue);
      setQuantityTypes(valueState)
    }
  }, [save, newTypesValue]);

  const resultParameter = getParameterByParameterCode(
    subProducts,
    "quantity"
  );
  useEffect(() => {
    if (resultParameter) {
      setQuantityState((state) => ({
        ...state,
        equalQuantity: Number(resultParameter?.values[0]),
      }));
    }
  }, [resultParameter]);

  const quantityTypesValues = useRecoilValue(
    tempProductQuantityTypesValuesState
  );
  const [valueState,setValuesState] = useRecoilState(tempProductQuantityTypesValuesState);
  const quantity = useMemo(() => {
    return quantityTypesValues
      .reduce((acc, val) => acc + val.quantity, 0)
      .toString();
  }, [quantityTypesValues]);
  const toggleDuplicateName = () => {
    setQuantityState((state) => ({
      ...state,
      duplicatedName: !state.duplicatedName,
    }));
  };
  const equalQuantityChange = (key: string, newValue: string) => {
    setValuesState((prevState) =>
      prevState.map((value) => ({ ...value, quantity: +newValue }))
    );
  };

  const onDeleteQuantityType = useCallback((index: number) => {
    debouncedHandleInputChange.cancel();
    setValuesState((prevState) => {
      if (index < 0 || index >= prevState.length) {
        return prevState;
      }
      let nextValues = prevState.filter((_, i) => i !== index);
      if (quantityState.duplicatedName) {
        nextValues = nextValues.map((value, i) => ({
          ...value,
          name: JobNameParameter?.values[0] + " " + (i + 1),
        }));
      }
      return nextValues;
    });
    setNewTypesValue((prev) => {
      const currentAmount = Number(prev);
      const nextAmount = Number.isFinite(currentAmount)
        ? Math.max(0, currentAmount - 1)
        : 0;
      return String(nextAmount);
    });
  }, [
    JobNameParameter?.values,
    debouncedHandleInputChange,
    quantityState.duplicatedName,
  ]);

  const inputs: QuantityTypesInputs[] = useMemo(() => {
    return [
      {
        name: "quantity",
        label: "quantityTypes.quantity",
        type: "number",
        placeholder: "quantityTypes.quantity",
        required: false,
        parameterKey: "quantity",
        value: quantity,
        options: [],
        isValid: true,
        onChange: () => {},
        readonly: true,
        
      },
      {
        name: "typeAmount",
        label: "quantityTypes.typeAmount",
        type: "number",
        placeholder: "quantityTypes.typeAmount",
        required: false,
        parameterKey: "typeAmount",
        value: newTypesValue,
        options: [],
        isValid: true,
        onChange:handleInputChange,
      },
      {
        name: "equalQuantity",
        label: "quantityTypes.equalQuantity",
        type: "number",
        placeholder: "quantityTypes.equalQuantity",
        required: false,
        parameterKey: "equalQuantity",
        value: valueState[0]?.quantity?.toString()||"0",
        options: [],
        isValid: true,
        onChange: equalQuantityChange,
      },
    ];
  }, [quantityTypesValues, quantityState, quantity, newTypesValue]);

  return {
    inputs,
    isDuplicateName: quantityState.duplicatedName,
    toggleDuplicateName,
    save,
    setSave,
    onDeleteQuantityType,
  };
};

export { useQuantityTypes };
